import { makeStyles } from '@material-ui/core/styles'
import { coachProfileSectionHeading } from '@lib/pga-coach/coach-profile-layout'

const leadFieldHeight = '3.75rem'
const leadFieldRadius = 14

const leadOutlinedFieldRoot = {
  borderRadius: leadFieldRadius,
  height: leadFieldHeight,
  minHeight: leadFieldHeight,
  backgroundColor: 'white',
  alignItems: 'center',
  boxSizing: 'border-box',
}

const leadOutlinedFieldLabel = (theme) => ({
  '& .MuiInputLabel-outlined': {
    '&:not(.MuiInputLabel-shrink)': {
      top: '50%',
      transform: `translate(${theme.spacing(2)}px, -50%)`,
    },
    '&.MuiInputLabel-shrink': {
      transform: `translate(${theme.spacing(2)}px, -6px) scale(0.75)`,
    },
  },
})

export const useStyles = makeStyles((theme) => ({
  root: {
    height: '150',
    overflow: 'visible',
  },
  subtext: {
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '25px',
  },
  /**
   * Tall rounded outlined fields aligned with coach journey hero search (white surface,
   * neutral border — not the journey pill fill color).
   */
  formInput: {
    width: '100%',
    ...leadOutlinedFieldLabel(theme),
    '& .MuiOutlinedInput-root': {
      ...leadOutlinedFieldRoot,
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(15, 50, 84, 0.12)',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(15, 50, 84, 0.22)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
      borderColor: theme.palette.blue.main,
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '1.125rem',
      fontWeight: 500,
      padding: `0 ${theme.spacing(2)}px`,
      height: '100%',
      boxSizing: 'border-box',
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      ...leadOutlinedFieldRoot,
      backgroundColor: theme.palette.common.white,
      padding: `0 ${theme.spacing(2)}px !important`,
    },
    '& .MuiAutocomplete-input': {
      fontSize: '1.125rem',
      fontWeight: 500,
      padding: '0 !important',
      minHeight: 'unset',
    },
  },
  leadFormOptInSpacing: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  /**
     * reCAPTCHA v2 checkbox: flagged `barePageLayout` passes `size="normal"`
     * (horizontal 304×78). Legacy keeps the Recaptcha default `compact`
     * (164×144). Cap width at 304px and avoid FormControl so a full-width
     * Grid column does not stretch the iframe.
     */
  recaptchaGridItem: {
    maxWidth: '100%',
  },
  recaptchaSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'inline-block',
    width: 'min(100%, 304px)',
    maxWidth: '100%',
    verticalAlign: 'top',
    lineHeight: 0,
    '& .g-recaptcha': {
      display: 'inline-block',
    },
  },
  recaptchaSubmitError: {
    marginTop: theme.spacing(1),
    lineHeight: 1.4,
  },
  goalSelectControl: {
    ...leadOutlinedFieldLabel(theme),
    '& .MuiOutlinedInput-root': {
      ...leadOutlinedFieldRoot,
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(15, 50, 84, 0.12)',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(15, 50, 84, 0.22)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
      borderColor: theme.palette.blue.main,
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
    '& .MuiSelect-select': {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      minHeight: leadFieldHeight,
      margin: 0,
      padding: `0 ${theme.spacing(4)}px 0 ${theme.spacing(2)}px`,
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.25,
      WebkitTapHighlightColor: 'transparent',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiInputBase-root.Mui-focused .MuiSelect-select': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(21, 47, 76, 0.55)',
      right: theme.spacing(0.5),
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  /** Match pga-coach-journey `searchMenuPaper` (rounded menu, subtle border + shadow). */
  selectMenuPaper: {
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(15, 50, 84, 0.08)',
    boxShadow: '0 6px 20px rgba(15, 34, 52, 0.12), 0 2px 6px rgba(15, 34, 52, 0.06)',
    minWidth: 260,
    marginTop: theme.spacing(1),
    '& ul': {
      padding: 0,
    },
  },
  selectMenuItem: {
    padding: '12px 16px',
    fontSize: '0.9375rem',
    '&:hover:not(.Mui-selected)': {
      backgroundColor: '#f0f2f5',
    },
    '&.Mui-selected': {
      backgroundColor: '#143d66',
      color: theme.palette.common.white,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#0f3254',
      },
    },
  },
  contactOverline: {
    /**
     * Canonical coach-profile eyebrow type system (see
     * `coach-tools-row.label`): overline font family, 12px, 700,
     * `0.12em` tracking, uppercase. Color references `blue.main` so
     * this overline ties to the active "Contact the Coach"
     * CategoryTabs pill the form sits beneath.
     */
    ...theme.typography.overline,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: theme.palette.blue.main,
    marginBottom: theme.spacing(1),
    lineHeight: 1.4,
  },
  contactHeadline: {
    ...theme.typography.h3,
    color: theme.palette.text.primary,
    margin: 0,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
      lineHeight: 1.2,
    },
  },
  /**
   * Flagged profile: same computed type as other flagged band titles
   * (`coachProfileSectionHeading` / visual h5). Not theme h3 display.
   */
  contactHeadlineFlagged: {
    ...coachProfileSectionHeading(theme),
    margin: 0,
    marginBottom: theme.spacing(1),
  },
  contactSubtitle: {
    ...theme.typography.body1,
    color: theme.palette.grey.darker,
    fontWeight: 400,
    maxWidth: 560,
    marginTop: 0,
    marginBottom: 0,
  },
  /** Flagged profile: helper spans the content column, not a 560px caption. */
  contactSubtitleFullWidth: {
    maxWidth: 'none',
  },
  /**
   * Flagged profile: title, fields, consent, and submit share one
   * full-width content-column panel.
   */
  flaggedFormPanel: {
    boxSizing: 'border-box',
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: 16,
    border: '1px solid rgba(15, 50, 84, 0.10)',
    backgroundColor: 'rgba(0, 35, 75, 0.03)',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  disableSpacing: {
    paddingLeft: '0',
    paddingRight: '0',
  },
  coachInfo: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    minHeight: '10rem',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
}))
